import { createProject, listProjects } from '../api/projectApi';
import { ProjectSummary } from '../core/types';
import { requireElement } from './dom';

export interface ProjectPickerCallbacks {
  onProjectSelected: (projectName: string | null) => void;
}

/**
 * GitHub-repo-style project picker: a dropdown of existing projects (which
 * also lists projects that already exist as directories on the Oracle VM,
 * auto-discovered by the backend) plus a "+ new project" inline form.
 */
export class ProjectPicker {
  private readonly select: HTMLSelectElement;
  private readonly newBtn: HTMLButtonElement;
  private readonly form: HTMLFormElement;
  private readonly nameInput: HTMLInputElement;
  private readonly descInput: HTMLInputElement;
  private readonly cancelBtn: HTMLButtonElement;
  private readonly errorEl: HTMLElement;
  private projects: ProjectSummary[] = [];

  constructor(private readonly callbacks: ProjectPickerCallbacks) {
    this.select = requireElement<HTMLSelectElement>('project-select');
    this.newBtn = requireElement<HTMLButtonElement>('new-project-btn');
    this.form = requireElement<HTMLFormElement>('new-project-form');
    this.nameInput = requireElement<HTMLInputElement>('new-project-name');
    this.descInput = requireElement<HTMLInputElement>('new-project-description');
    this.cancelBtn = requireElement<HTMLButtonElement>('new-project-cancel');
    this.errorEl = requireElement<HTMLElement>('project-error');

    this.select.addEventListener('change', () => {
      this.callbacks.onProjectSelected(this.select.value || null);
    });
    this.newBtn.addEventListener('click', () => this.form.classList.toggle('hidden'));
    this.cancelBtn.addEventListener('click', () => {
      this.form.classList.add('hidden');
      this.form.reset();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.handleCreate();
    });
  }

  async load(): Promise<void> {
    this.projects = await listProjects();
    this.render();
    const selected = this.projects[0]?.name ?? null;
    if (selected) this.select.value = selected;
    this.callbacks.onProjectSelected(selected);
  }

  getSelected(): string | null {
    return this.select.value || null;
  }

  private render(): void {
    this.select.innerHTML = '';
    if (this.projects.length === 0) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = '프로젝트가 없습니다 — 먼저 만들어주세요';
      this.select.appendChild(option);
      return;
    }
    for (const project of this.projects) {
      const option = document.createElement('option');
      option.value = project.name;
      option.textContent = project.description ? `${project.name} — ${project.description}` : project.name;
      this.select.appendChild(option);
    }
  }

  private async handleCreate(): Promise<void> {
    const name = this.nameInput.value.trim();
    if (!name) return;
    this.setError(null);

    try {
      const project = await createProject(name, this.descInput.value.trim() || undefined);
      this.projects.push(project);
      this.projects.sort((a, b) => a.name.localeCompare(b.name));
      this.render();
      this.select.value = project.name;
      this.callbacks.onProjectSelected(project.name);
      this.form.reset();
      this.form.classList.add('hidden');
    } catch (error) {
      this.setError((error as Error).message);
    }
  }

  private setError(message: string | null): void {
    this.errorEl.textContent = message ?? '';
    this.errorEl.classList.toggle('hidden', !message);
  }
}
